import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { useSesion } from "@/modules/auth/useSesion";
import { AnularCotizacion } from "@/modules/cotizador/AnularCotizacion";
import { CambiarClienteDeLaOrden } from "./CambiarClienteDeLaOrden";
import { DocumentosDeLaObra } from "./DocumentosDeLaObra";
import { EnlazarFactura } from "./EnlazarFactura";
import { HojaDeLaOrden } from "@/modules/cotizaciones/hoja/HojaDeLaOrden";
import { HojaDeLaObra } from "@/modules/cotizaciones/hoja/HojaDeLaObra";
import { ResumenDeLaCotizacion } from "@/modules/cotizaciones/hoja/ResumenDeLaCotizacion";
import { Cotizador } from "@/modules/cotizador/Cotizador";
import { TarjetaPrecierre } from "@/modules/precierre/TarjetaPrecierre";
import { Programar } from "@/modules/programacion/Programar";
import { Bitacora } from "@/shared/dominio/Bitacora/Bitacora";
import { ESTADOS_ORDEN_SERVICIO } from "@/shared/dominio/estados";
import {
  CotizacionFallida,
  DecidirCotizacion,
} from "@/modules/cotizaciones/DecidirCotizacion";
import { EnlazarCotizacion } from "@/modules/cotizaciones/EnlazarCotizacion";
import { useAdmiteAdicional } from "@/modules/cotizaciones/useEnlazarCotizacion";
import { Aviso } from "@/shared/ui/Aviso/Aviso";
import { Boton } from "@/shared/ui/Boton/Boton";
import { Cargando } from "@/shared/ui/Estados/Cargando";
import { Pastilla } from "@/shared/ui/Pastilla/Pastilla";
import { Tarjeta } from "@/shared/ui/Tarjeta/Tarjeta";
import { fecha, fechaHora, pesos } from "@/shared/utilidades/formato";

import { CabeceraOrden } from "./CabeceraOrden";
import { EditarDescripcion } from "./EditarDescripcion";
import { EditarDireccion } from "./EditarDireccion";
import { EditarUbicacion } from "./EditarUbicacion";
import { WhatsAppDeEnvio } from "./WhatsAppDeEnvio";
import { ProgramarVisita } from "@/modules/visitas/ProgramarVisita";
import { ResultadoDeVisita } from "@/modules/visitas/ResultadoDeVisita";
import { FotosOrden } from "./FotosOrden";
import {
  useAccionesOrdenServicio,
  useOrdenServicio,
} from "./useOrdenesServicio";
import estilos from "./DetalleOrdenServicio.module.css";

/** Un dato de la ficha, con su rótulo. */
function Dato({
  rotulo,
  children,
  completo = false,
}: {
  rotulo: string;
  children: ReactNode;
  completo?: boolean;
}) {
  const vacio = children === null || children === undefined || children === "";

  return (
    <div className={`${estilos.dato} ${completo ? estilos.datoCompleto : ""}`}>
      <p className={estilos.rotulo}>{rotulo}</p>
      <p className={`${estilos.valor} ${vacio ? estilos.valorVacio : ""}`}>
        {vacio ? "Sin registrar" : children}
      </p>
    </div>
  );
}

/**
 * Detalle de una orden de servicio.
 *
 * Muestra el documento y su historia completa. Las acciones disponibles salen
 * del estado en que está: no se ofrecen botones que la base va a rechazar,
 * porque un botón que a veces falla enseña a desconfiar de todos.
 */
export function DetalleOrdenServicio() {
  const { id } = useParams<{ id: string }>();
  const { puede } = useSesion();

  const { orden, cargando, error } = useOrdenServicio(id);
  const { cambiarEstado } = useAccionesOrdenServicio();

  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [cotizando, setCotizando] = useState(false);
  const [modificando, setModificando] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [viendoHoja, setViendoHoja] = useState(false);
  const [viendoObra, setViendoObra] = useState(false);
  const [cambiandoCliente, setCambiandoCliente] = useState(false);
  const [programando, setProgramando] = useState(false);
  // La ventana para escoger la cotización de World Office que le pone precio.
  //
  // AQUÍ ARRIBA, CON LOS DEMÁS, Y NO DONDE SE USA. Lo puse más abajo, junto a
  // los permisos, y quedó DESPUÉS de los dos `return` tempranos de esta
  // función —el de «cargando» y el de «no se encontró»—. Un hook detrás de un
  // return condicional cambia el número de hooks entre renders: React lo corta
  // en seco con el error 310 y desmonta el árbol ENTERO, así que la pantalla se
  // quedaba en blanco, sin menú ni barra, y sin decir por qué.
  const [enlazando, setEnlazando] = useState(false);
  const [enlazandoFactura, setEnlazandoFactura] = useState(false);
  // Y la de los adicionales, que es otra ventana con otro sentido: aquella
  // pone precio al trabajo, esta cobra lo que apareció después.
  const [anadiendo, setAnadiendo] = useState(false);
  // El otro camino del adicional: armarlo aquí con el catálogo, en vez de
  // enlazar uno que ya existe en World Office.
  const [armandoAdicional, setArmandoAdicional] = useState(false);

  const { data: admiteAdicional = false } = useAdmiteAdicional(id ?? null);

  /*
   * EL HUECO SE TAPA MIDIENDO, NO ADIVINANDO.
   *
   * Dos intentos fallaron antes: hacer que la izquierda acompañara el scroll
   * —y recortaba los botones del recorrido— y colocar la historia con una
   * regla fija según el estado —y cada estado nuevo dejaba el hueco en otro
   * lado: en una orden cotizada sin orden de trabajo la derecha seguía casi
   * vacía—. Ahora se miden las dos columnas sin estos dos bloques y cada uno
   * va a la que quede más corta. Vale para cualquier orden en cualquier
   * estado, y no recorta nada: los bloques se mueven enteros.
   */
  const baseIzquierda = useRef<HTMLDivElement>(null);
  const baseDerecha = useRef<HTMLDivElement>(null);
  const bloqueDocumentos = useRef<HTMLDivElement>(null);
  const bloqueHistoria = useRef<HTMLDivElement>(null);
  const [reparto, setReparto] = useState<{
    docs: "izq" | "der";
    historia: "izq" | "der";
  }>({
    docs: "izq",
    historia: "izq",
  });

  useEffect(() => {
    const medir = () => {
      const alto = (r: { current: HTMLDivElement | null }) =>
        r.current?.offsetHeight ?? 0;
      let izq = alto(baseIzquierda);
      let der = alto(baseDerecha);
      // Los bloques, a la columna más corta en cada paso: primero el más
      // alto, para que el que viene detrás afine.
      // Si los documentos ya van fijos dentro de la columna derecha (cotización
      // aprobada), su alto ya está en esa base: no se reparte otra vez.
      const docs =
        bloqueDocumentos.current &&
        baseDerecha.current?.contains(bloqueDocumentos.current)
          ? 0
          : alto(bloqueDocumentos);
      const historia = alto(bloqueHistoria);
      const nuevo = {
        docs: "izq" as "izq" | "der",
        historia: "izq" as "izq" | "der",
      };
      for (const [clave, h] of (
        [
          ["docs", docs],
          ["historia", historia],
        ] as const
      )
        .slice()
        .sort((a, b) => b[1] - a[1])) {
        if (izq <= der) {
          nuevo[clave] = "izq";
          izq += h;
        } else {
          nuevo[clave] = "der";
          der += h;
        }
      }
      setReparto((previo) =>
        previo.docs === nuevo.docs && previo.historia === nuevo.historia
          ? previo
          : nuevo
      );
    };

    medir();
    // Las bases y los bloques cambian de alto al cargar sus datos: se vuelve
    // a medir cada vez, sin bucle, porque el alto de las bases no depende de
    // dónde queden los bloques.
    const vigia = new ResizeObserver(medir);
    for (const r of [
      baseIzquierda,
      baseDerecha,
      bloqueDocumentos,
      bloqueHistoria,
    ]) {
      if (r.current) vigia.observe(r.current);
    }
    return () => vigia.disconnect();
  }, [orden?.id]);

  if (cargando) return <Cargando mensaje="CARGANDO DETALLE ORDEN..." />;

  if (error || !orden) {
    return (
      <Aviso tipo="error" titulo="No se encontró la orden">
        {error ??
          "Puede que se haya eliminado o que su rol no tenga acceso a ella."}
      </Aviso>
    );
  }

  const presentacion = ESTADOS_ORDEN_SERVICIO[orden.estado];

  // TRES PERMISOS Y NO UNO. Antes todo el detalle —incluidas las acciones
  // sobre la cotización— se gobernaba con `ordenes_servicio.editar`, que es el
  // permiso del módulo equivocado. Consecuencia: el coordinador operativo, que
  // es quien arma y ajusta las cotizaciones, no veía ninguna acción sobre
  // ellas; y quien decide la compuerta —gerencia— no tenía dónde decidir.
  const puedeEditar = puede("ordenes_servicio", "editar");
  const puedeEditarCotizacion = puede("cotizaciones", "editar");
  const puedeAprobarCotizacion = puede("cotizaciones", "aprobar");
  // Programar es del módulo «programacion», no del de órdenes de servicio.
  // Con el permiso equivocado, el rol que se llama programación no veía el
  // botón de programar.
  const puedeProgramar = puede("programacion", "editar");

  async function mover(estado: "pendiente_cotizar" | "anulada") {
    setErrorAccion(null);

    try {
      await cambiarEstado.mutateAsync({ id: orden!.id, estado });
    } catch (fallo) {
      setErrorAccion(
        fallo instanceof Error
          ? fallo.message
          : "No fue posible cambiar el estado."
      );
    }
  }

  /*
   * LA TARJETA DE LA COTIZACIÓN, SOLO MIENTRAS HAYA ALGO QUE HACER CON ELLA.
   *
   * Johan, sobre la FEV 2981 aprobada: «¿por qué me sale arriba? Debería
   * salirme los documentos de la obra; eso sobra». Aprobada, la tarjeta solo
   * repetía el valor y los mismos renglones que «Documentos de la obra», donde
   * esa misma cotización se abre. Mientras está en curso —sin precio, a
   * medias, esperando a World Office o la decisión de gerencia o del
   * cliente— sí manda, porque ahí están sus botones.
   */
  const cotizacionCerrada = orden?.cotizacion?.estado === "aprobada";

  const documentosDeLaObra = (
    <div ref={bloqueDocumentos}>
      <Tarjeta
        titulo="Documentos de la obra"
        subtitulo="Cotizaciones, adicionales y facturas · toque uno para ver qué tiene dentro"
      >
        <DocumentosDeLaObra
          ordenServicioId={orden.id}
          puedeEnlazar={puedeEditarCotizacion}
          onEnlazarCotizacion={() => setEnlazando(true)}
          onEnlazarFactura={() => setEnlazandoFactura(true)}
          // Los adicionales se añaden aquí mismo, en la misma fila que el
          // resto de botones: antes iban en otra fila y uno no parecía botón.
          onArmarAdicional={
            admiteAdicional ? () => setArmandoAdicional(true) : undefined
          }
          onEnlazarAdicional={
            admiteAdicional ? () => setAnadiendo(true) : undefined
          }
          // La obra entera —no solo la principal—: con un documento abre ese
          // mismo, con varios el consolidado. Vivía en la tarjeta de la
          // cotización, que se esconde una vez aprobada.
          onVerDocumento={
            cotizacionCerrada && (orden.cotizacion?.valor_total ?? 0) > 0
              ? () => setViendoObra(true)
              : undefined
          }
        />
      </Tarjeta>
    </div>
  );

  const historiaAncha = (
    <div ref={bloqueHistoria} className={estilos.historiaAncha}>
      <Tarjeta
        titulo="Historia del documento"
        subtitulo="Quién hizo qué, cuándo y con qué justificación"
      >
        <Bitacora tipo="orden_servicio" documentoId={orden.id} />
      </Tarjeta>
    </div>
  );

  return (
    <>
      {/* EL VOLVER NO SALE EN PAPEL: es un mando, y en una hoja no se pulsa.

          Aquí hubo un botón de imprimir la orden, pedido y luego retirado por
          Johan: lo que hace falta en papel es la COTIZACIÓN —la que ve el
          cliente y la que bodega lee para alistar—, y esa tiene su propio
          botón en la tarjeta de la cotización. Imprimir además la pantalla de
          trabajo no le servía a nadie y eran dos botones que se parecían. */}
      <div className={`${estilos.barraDeArriba} no-imprimir`}>
        <Link to="/ordenes" className={estilos.volver}>
          ← Volver a la matriz
        </Link>
      </div>

      <div className={estilos.columnas}>
        {/* LA IZQUIERDA TAMBIÉN ES UNA COLUMNA.
            Era una sola tarjeta, así que todo lo demás se apilaba a la
            derecha: en ejecución medía 943 contra 1.721, casi el doble, y la
            página se leía torcida. Johan lo dijo tal cual: «la derecha está
            súper larga». */}
        <div className={estilos.columnaIzquierda}>
          <div ref={baseIzquierda} className={estilos.columnaIzquierda}>
            <Tarjeta
              titulo="Orden de servicio"
              subtitulo="Documento 1 de la cadena · Estación 1 del recorrido"
              acciones={
                <div className={estilos.encabezado}>
                  <span className={estilos.numero}>{orden.numero}</span>
                  <Pastilla
                    tono={presentacion.tono}
                    titulo={presentacion.explicacion}
                  >
                    {presentacion.etiqueta}
                  </Pastilla>
                </div>
              }
            >
              {errorAccion && (
                <div className={estilos.avisoAccion}>
                  <Aviso tipo="error">{errorAccion}</Aviso>
                </div>
              )}

              {/* QUIÉN, DÓNDE VA Y CUÁNTO VALE, con el peso que tienen.
              Antes esto era una banda lila con el nombre a 14 px y debajo una
              tabla de filas gris sobre blanco: la pantalla del documento más
              importante del sistema no decía una sola cifra, y todo lo que
              faltaba —lo que vale, cuánto lleva parado, en qué estación va—
              existía y había que ir a buscarlo a otras tres pantallas. */}
              <CabeceraOrden orden={orden} />

              <div className={estilos.datos}>
                <Dato rotulo="Documento">{orden.cliente?.documento}</Dato>

                <div className={`${estilos.dato} ${estilos.datoCompleto}`}>
                  <p className={estilos.rotulo}>Dirección</p>
                  <EditarDireccion
                    ordenServicioId={orden.id}
                    direccion={orden.direccion}
                    puedeEditar={puedeEditar && orden.estado !== "anulada"}
                  />
                </div>

                {puedeEditar && orden.estado !== "anulada" && (
                  <div className={estilos.datoCompleto}>
                    <EditarUbicacion
                      ordenId={orden.id}
                      direccion={orden.direccion}
                      // La orden no tiene ciudad propia: la de su zona o, si
                      // no, la del cliente (que viene de WO, 0271). Antes
                      // leía `orden.ciudad`, que no existe, y Google buscaba
                      // en «Colombia».
                      ciudad={
                        orden.zona?.ciudad ?? orden.cliente?.ciudad ?? null
                      }
                      departamento={
                        orden.zona?.ciudad
                          ? orden.zona.departamento
                          : orden.cliente?.departamento ?? null
                      }
                      latitud={orden.latitud}
                      longitud={orden.longitud}
                      origen={orden.ubicacion_origen}
                    />
                  </div>
                )}

                {/* A qué WhatsApp salen la cotización y el acta (0273). */}
                <div className={`${estilos.dato} ${estilos.datoCompleto}`}>
                  <p className={estilos.rotulo}>
                    WhatsApp para cotización y acta
                  </p>
                  <div className={estilos.valor}>
                    <WhatsAppDeEnvio ordenServicioId={orden.id} sinRotulo />
                  </div>
                </div>

                {/* LA VISITA: quién va, qué día y a qué hora (0256).
                Va con los datos de la orden y no entre los botones: es un
                dato de la orden —quién fue a mirarla y cuándo—, no una
                acción. Una vez hecha, lo que el técnico registró. */}
                {orden.visita_hecha_en ? (
                  <div className={estilos.anchoCompleto}>
                    <ResultadoDeVisita
                      hechaEn={orden.visita_hecha_en}
                      observacion={orden.visita_observacion}
                      quiereCotizar={orden.visita_quiere_cotizar}
                      quien={orden.cotiza_colaborador?.nombre ?? null}
                    />
                  </div>
                ) : (
                  ["creada", "pendiente_cotizar"].includes(orden.estado) && (
                    <div className={estilos.anchoCompleto}>
                      <ProgramarVisita
                        ordenServicioId={orden.id}
                        colaboradorId={orden.cotiza_colaborador_id ?? null}
                        nombreActual={orden.cotiza_colaborador?.nombre ?? null}
                        inicioActual={orden.visita_inicio ?? null}
                        horasActual={
                          orden.visita_horas === null ||
                          orden.visita_horas === undefined
                            ? null
                            : Number(orden.visita_horas)
                        }
                        puedeEditar={puedeEditarCotizacion}
                      />
                    </div>
                  )
                )}

                <Dato rotulo="Fecha de solicitud">
                  {fecha(orden.fecha_solicitud)}
                </Dato>
                <Dato rotulo="Registrada el">{fechaHora(orden.creado_en)}</Dato>

                {/* Editable desde aquí (0265): Johan, «¿cómo lo edito?». */}
                <Dato rotulo="Descripción del trabajo" completo>
                  <EditarDescripcion
                    ordenServicioId={orden.id}
                    descripcion={orden.descripcion}
                    puedeEditar={puedeEditar && orden.estado !== "anulada"}
                  />
                </Dato>

                {orden.notas && (
                  <Dato rotulo="Notas internas" completo>
                    {orden.notas}
                  </Dato>
                )}
              </div>

              {/* ── Lo que sigue en el recorrido ─────────────────────────────── */}
              {puedeEditar && orden.estado !== "anulada" && (
                <div className={estilos.acciones}>
                  {/* Con un borrador ya empezado, «Enviar a cotizar» sobra:
                  alguien ya está cotizando. */}
                  {orden.estado === "creada" && !orden.cotizacion && (
                    <Boton
                      variante="principal"
                      onClick={() => void mover("pendiente_cotizar")}
                      trabajando={cambiarEstado.isPending}
                    >
                      Enviar a cotizar
                    </Boton>
                  )}

                  {/* Estación 2 por el camino nuevo: se arma la cotización aquí y
                  nace ya amarrada, sin salir a buscarla después. Un borrador a
                  medias se retoma por el mismo botón: el cotizador lo reabre. */}
                  {/* Y LA QUE EL CLIENTE RECHAZÓ TAMBIÉN SE PUEDE REHACER.
                  No modificándola —ese documento es el que él vio y al que
                  dijo que no; cambiarle los renglones dejaría su número
                  señalando otra cosa— sino con una nueva. La base ya lo tenía
                  previsto: el índice de una sola cotización por orden excluye
                  las rechazadas. Sin este botón la orden quedaba en un
                  callejón: ni rehacer ni cerrar. */}
                  {/* UN BORRADOR SE RETOMA DESDE LA TARJETA DE COTIZACIÓN,
                  junto al aviso que lo nombra; aquí salía repetido (Johan:
                  «eso quedó en 2 lados»). Aquí queda para empezar una nueva o
                  rehacer la que el cliente rechazó. */}
                  {orden.estado === "pendiente_cotizar" &&
                    (!orden.cotizacion ||
                      orden.cotizacion.estado === "rechazada") && (
                      <Boton
                        variante="principal"
                        onClick={() => setCotizando(true)}
                      >
                        {orden.cotizacion?.estado === "rechazada"
                          ? "Cotizar de nuevo"
                          : "Cotizar"}
                      </Boton>
                    )}

                  {/* CAMBIAR EL CLIENTE. Hace falta sobre todo cuando la orden se
                  levantó a nombre de CONSUMIDOR FINAL —el mostrador— y el
                  cliente real se supo después: a ese genérico no se le puede
                  mandar la cotización porque no tiene teléfono. */}
                  <Boton
                    variante="secundario"
                    onClick={() => setCambiandoCliente(true)}
                  >
                    Cambiar cliente
                  </Boton>

                  {orden.estado !== "cotizada" && (
                    <Boton
                      variante="peligro"
                      onClick={() => void mover("anulada")}
                      trabajando={cambiarEstado.isPending}
                    >
                      Anular
                    </Boton>
                  )}
                </div>
              )}
            </Tarjeta>

            {/* ── Lo que se vio en la visita ───────────────────────────────
              Van en la columna de la izquierda, pegadas al documento 1: son
              las fotos de ANTES de tocar nada y pertenecen a la orden de
              servicio, no al trabajo. Las de la ejecución son otras y viven en
              el acta.

              El comentario decía esto mismo desde el principio y la tarjeta
              estaba a la derecha: alguien la movió y el comentario se quedó
              contando otra cosa. */}
            <Tarjeta
              titulo="Fotos de la visita"
              subtitulo="Cómo estaba la instalación antes de empezar"
            >
              <FotosOrden
                ordenServicioId={orden.id}
                puedeEditar={puedeEditar}
              />
            </Tarjeta>
          </div>
          {!cotizacionCerrada && reparto.docs === "izq" && documentosDeLaObra}
          {reparto.historia === "izq" && historiaAncha}
        </div>

        <div className={estilos.columnaDerecha}>
          <div ref={baseDerecha} className={estilos.columnaDerecha}>
            {/* Con la cotización aprobada, arriba van los documentos de la
                obra en su lugar: es lo que se quiere ver de una obra en curso. */}
            {cotizacionCerrada && documentosDeLaObra}

            {/* ── Documento 2, mientras haya algo que hacer con él ──────── */}
            {!cotizacionCerrada && (
              <Tarjeta
                titulo="Cotización"
                subtitulo="Documento 2 · El puente con World Office"
                /* Viva = aquí alguien tiene que decidir. Es la compuerta 1: la
               cotización está lista y espera que la aprueben o la rechacen. */
                tono={
                  orden.cotizacion?.estado === "cotizada" ? "viva" : "normal"
                }
              >
                {orden.cotizacion ? (
                  orden.cotizacion.estado === "borrador" ? (
                    <>
                      <Aviso tipo="alerta" titulo="Cotización a medias">
                        Quedó empezada y sin precio cerrado. Con{" "}
                        <b>Seguir cotizando</b> se retoma donde quedó, o se
                        enlaza una que ya esté hecha en World Office.
                      </Aviso>

                      {/* EL OTRO CAMINO, QUE AQUÍ NO ESTABA.
                      El botón de enlazar vivía solo en la rama de «esta orden
                      no tiene cotización», así que en cuanto alguien abría el
                      cotizador —y eso ya deja un borrador— desaparecía: la
                      orden se quedaba sin forma de adoptar un documento hecho
                      en World Office. Johan se topó con eso en la OS-2026-0056.

                      La base nunca lo prohibió: `fn_enlazar_cotizacion` ADOPTA
                      el borrador —le pone el número y el asiento— y solo se
                      niega si la cotización ya tiene documento detrás. */}
                      {puedeEditarCotizacion && (
                        <div className={estilos.accionesMenores}>
                          {/* El que el aviso nombra, aquí mismo. */}
                          <Boton
                            variante="principal"
                            onClick={() => setCotizando(true)}
                          >
                            Seguir cotizando
                          </Boton>
                          <Boton
                            variante="secundario"
                            onClick={() => setEnlazando(true)}
                          >
                            Enlazar una que ya existe
                          </Boton>
                        </div>
                      )}
                    </>
                  ) : orden.cotizacion.estado === "pendiente" ? (
                    <Aviso tipo="info" titulo="Esperando a World Office">
                      La cotización ya se pidió y está en la cola. Cuando World
                      Office devuelva el número, aparecerá aquí sola.
                    </Aviso>
                  ) : orden.cotizacion.estado === "fallida" ? (
                    /* World Office no la creó. Antes esto se quedaba en «pendiente»
                   para siempre y la pantalla seguía diciendo que esperara un
                   número que nunca iba a llegar. */
                    <CotizacionFallida
                      cotizacionId={orden.cotizacion.id}
                      puedeReintentar={puedeEditarCotizacion}
                      onError={setErrorAccion}
                    />
                  ) : (
                    <>
                      {/* La cifra primero: es con lo que se decide aprobar o
                      rechazar. El número del documento y el estado la
                      acompañan, no compiten con ella. */}
                      <div className={estilos.plata}>
                        <span className={estilos.plataCifra}>
                          {pesos(orden.cotizacion.valor_total)}
                        </span>
                        <span className={estilos.plataPie}>
                          <span className={estilos.plataNumero}>
                            {orden.cotizacion.numero_cotizacion}
                          </span>
                          <span>·</span>
                          <span>
                            {orden.cotizacion.estado.replace(/_/g, " ")}
                          </span>
                        </span>
                      </div>

                      {/* EL DOCUMENTO SE MIRA, NO SE IMPRIME DESDE AQUÍ.
                      Aquí hubo un botón de imprimir y Johan pidió quitarlo
                      dos veces: esta pantalla es para llevar la orden. Pero
                      mirar no es imprimir, y aprobar sin ver lo que se
                      aprueba sí era un problema suyo: «acá no pueden ver la
                      cotización que van a aprobar». Se abre la misma hoja de
                      la sección de Cotizaciones, encima y sin salir de la
                      orden; el imprimir vive dentro de ella, que es su
                      sitio. */}
                      {(orden.cotizacion.valor_total ?? 0) > 0 && (
                        <>
                          <ResumenDeLaCotizacion orden={orden} />
                        </>
                      )}

                      {/* SIN WORLD OFFICE DETRÁS NO HAY PRECIO, HAY UN CERO.
                      Las órdenes que entraron por el Excel traen una
                      cotización provisional —«PROV-OT-816442», valor cero— y
                      la tarjeta las mostraba como si valieran cero pesos. No
                      es lo mismo: una dice que el trabajo es gratis y la otra
                      que nadie ha enlazado el documento todavía. */}
                      {/* SOLO CUANDO DE VERDAD NO HAY PRECIO.
                      Este aviso se escribió para las provisionales del import
                      —«PROV-…», valor cero— y con el cotizador propio empezó a
                      salir sobre cotizaciones armadas aquí, que SÍ tienen
                      número y valor. Decirle a alguien que su cotización de
                      setecientos noventa mil «sale en cero» no es un texto
                      desactualizado: es información falsa en la pantalla donde
                      se decide la plata.
                      La prueba es el valor, no el enlace: sin World Office
                      detrás puede haber precio de sobra. */}
                      {(orden.cotizacion.valor_total ?? 0) <= 0 && (
                        <>
                          <Aviso
                            tipo="alerta"
                            titulo="Todavía sin cotización de World Office"
                          >
                            Este número es provisional y por eso el valor sale
                            en cero. La cotización de verdad está en World
                            Office; al enlazarla entra el precio aquí, en el
                            tablero y en lo que ve el cliente.
                          </Aviso>

                          {puedeEditarCotizacion && (
                            <div className={estilos.acciones}>
                              <Boton onClick={() => setEnlazando(true)}>
                                Enlazar cotización
                              </Boton>
                            </div>
                          )}
                        </>
                      )}

                      {/* LA COMPUERTA 1, aquí y no solo en el tablero.
                      Es lo que MEGA reportó: se miraba la cotización en esta
                      pantalla y no había forma de aceptarla para que pasara a
                      programación. Gerencia aprueba y se la manda al cliente;
                      es la aceptación de él la que hace nacer la orden de
                      trabajo, no el visto bueno de la casa. */}
                      {puedeAprobarCotizacion &&
                        (orden.cotizacion.estado === "cotizada" ||
                          orden.cotizacion.estado === "enviada") && (
                          <DecidirCotizacion
                            cotizacionId={orden.cotizacion.id}
                            yaEnviada={orden.cotizacion.estado === "enviada"}
                            onListo={() => setErrorAccion(null)}
                            onError={setErrorAccion}
                          />
                        )}

                      {/* Si quien mira no es quien decide, se le dice de quién
                      depende. Sin esto la tarjeta queda con un hueco y nadie
                      sabe por qué no hay botón ni a quién hay que empujar. */}
                      {!puedeAprobarCotizacion &&
                        orden.cotizacion.estado === "cotizada" && (
                          <Aviso tipo="info">
                            Lista y esperando la decisión de <b>gerencia</b>: es
                            quien la aprueba y se la envía al cliente.
                          </Aviso>
                        )}

                      {!puedeAprobarCotizacion &&
                        orden.cotizacion.estado === "enviada" && (
                          <Aviso tipo="info">
                            Aprobada por gerencia y enviada al <b>cliente</b>.
                            El trabajo pasa a programación cuando él la acepte.
                          </Aviso>
                        )}

                      {/* Y QUÉ SE HACE AHORA, que es lo que se pregunta al leerlo.
                      Johan: «¿cómo quito esta de la matriz si no se logró un
                      acuerdo?». Son dos salidas y ninguna estaba escrita: o se
                      responde con otra propuesta, o se cierra la orden. */}
                      {orden.cotizacion.estado === "rechazada" && (
                        <Aviso
                          tipo="error"
                          titulo="El cliente rechazó la cotización"
                        >
                          {orden.cotizacion.notas_decision
                            ? orden.cotizacion.notas_decision
                            : "El cliente no aceptó esta propuesta."}{" "}
                          Si hay otra propuesta, use <b>Cotizar de nuevo</b>; si
                          no se llegó a un acuerdo, <b>anule la orden</b> y sale
                          de la matriz.
                        </Aviso>
                      )}

                      {/* Hasta la aprobación se puede ajustar; después no, porque
                      ya nació la orden de trabajo con ese precio. */}
                      {/* EL DOCUMENTO EN UN SOLO RENGLÓN: verlo, ajustarlo y,
                      apartado a la derecha, anularlo. «Ver el documento
                      completo» iba solo y centrado entre el resumen y los
                      botones de decidir, y Modificar/Anular en otro renglón
                      más abajo: tres alturas para un mismo documento. Johan:
                      «se ve desordenado». */}
                      {((orden.cotizacion.valor_total ?? 0) > 0 ||
                        (puedeEditarCotizacion &&
                          orden.cotizacion.estado === "cotizada")) && (
                        <div className={estilos.accionesMenores}>
                          {(orden.cotizacion.valor_total ?? 0) > 0 && (
                            <Boton
                              variante="secundario"
                              onClick={() => setViendoHoja(true)}
                            >
                              Ver el documento completo
                            </Boton>
                          )}
                          {puedeEditarCotizacion &&
                            orden.cotizacion.estado === "cotizada" && (
                              <>
                                <Boton
                                  variante="secundario"
                                  onClick={() => setModificando(true)}
                                >
                                  Modificar
                                </Boton>
                                <span
                                  className={estilos.empuje}
                                  aria-hidden="true"
                                />
                                {/* Anular conserva su señal de peligro: es
                                destructivo. */}
                                <Boton
                                  variante="peligro"
                                  onClick={() => setAnulando(true)}
                                >
                                  Anular
                                </Boton>
                              </>
                            )}
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <>
                    {/* ESTE TEXTO DECÍA QUE COTIZAR CREABA EL DOCUMENTO EN WORLD
                    OFFICE. Era cierto hasta que el flujo cambió: ahora la
                    cotización se arma aquí y viaja a contabilidad cuando
                    gerencia la aprueba. Dejarlo habría hecho que alguien no
                    cotizara por miedo a ensuciar World Office con algo que el
                    cliente todavía no ha aceptado. */}
                    <Aviso tipo="info">
                      Esta orden todavía no tiene precio. Con <b>Cotizar</b> se
                      arma aquí mismo, con el catálogo y su mano de obra. No se
                      crea nada en World Office todavía: allá llega cuando
                      gerencia la apruebe, y en ese mismo momento le sale al
                      cliente para que la acepte.
                    </Aviso>

                    {/* El otro camino, que faltaba: la cotización YA existe en
                    World Office —la hicieron allá— y lo único que falta es
                    decir a qué orden corresponde. */}
                    {puedeEditarCotizacion && (
                      <div className={estilos.acciones}>
                        <Boton
                          variante="secundario"
                          onClick={() => setEnlazando(true)}
                        >
                          Enlazar una que ya existe
                        </Boton>
                      </div>
                    )}
                  </>
                )}
              </Tarjeta>
            )}

            {/* ── Documento 3 · la orden de trabajo y su compuerta ────────── */}
            {orden.orden_trabajo && (
              <div>
                {/* Estación 3. Recién nacida no tiene quién ni cuándo: eso es lo
                  único que cabe hacerle, y se hace desde aquí. */}
                {orden.orden_trabajo.estado === "creada" ? (
                  <Tarjeta
                    titulo="Orden de trabajo"
                    subtitulo={`${orden.orden_trabajo.numero} · Estación 3 · falta programarla`}
                  >
                    <Aviso tipo="alerta" titulo="Sin colaborador ni fecha">
                      La cotización se aprobó y nació la orden de trabajo, pero
                      nadie la tiene asignada todavía. Mientras no se programe
                      no hay contra qué medir el trabajo.
                    </Aviso>

                    {puedeProgramar && (
                      <div className={estilos.acciones}>
                        <Boton onClick={() => setProgramando(true)}>
                          Programar
                        </Boton>
                      </div>
                    )}
                  </Tarjeta>
                ) : (
                  <TarjetaPrecierre orden={orden} />
                )}
              </div>
            )}
          </div>
          {!cotizacionCerrada && reparto.docs === "der" && documentosDeLaObra}
          {reparto.historia === "der" && historiaAncha}
        </div>
      </div>

      <EnlazarCotizacion
        ordenServicioId={orden.id}
        orden={orden.numero}
        cliente={orden.cliente?.nombre}
        abierto={enlazando}
        onCerrar={() => setEnlazando(false)}
      />

      {/* El otro camino, el que faltaba: la obra también se factura. */}
      <EnlazarFactura
        ordenServicioId={orden.id}
        clienteNombre={orden.cliente?.nombre ?? "este cliente"}
        abierto={enlazandoFactura}
        onCerrar={() => setEnlazandoFactura(false)}
      />

      <EnlazarCotizacion
        ordenServicioId={orden.id}
        orden={orden.numero}
        cliente={orden.cliente?.nombre}
        adicional
        abierto={anadiendo}
        onCerrar={() => setAnadiendo(false)}
      />

      {armandoAdicional && (
        <Cotizador
          abierto
          adicional
          ordenServicioId={orden.id}
          ordenNumero={orden.numero}
          clienteNombre={orden.cliente?.nombre ?? "Sin cliente"}
          onCerrar={() => setArmandoAdicional(false)}
        />
      )}

      {/* La historia va a lo ancho y debajo de los documentos: crece con el
          tiempo, se lee de corrido y así deja de ser una columna corta que
          dejaba medio lienzo en blanco al lado. */}
      <div className={`${estilos.historia} ${estilos.historiaAngosta}`}>
        <Tarjeta
          titulo="Historia del documento"
          subtitulo="Quién hizo qué, cuándo y con qué justificación"
        >
          <Bitacora tipo="orden_servicio" documentoId={orden.id} />
        </Tarjeta>
      </div>

      {orden.orden_trabajo && (
        <Programar
          abierto={programando}
          ordenTrabajoId={orden.orden_trabajo.id}
          ordenTrabajoNumero={orden.orden_trabajo.numero}
          ordenServicioId={orden.id}
          horasProyectadas={orden.cotizacion?.horas_proyectadas ?? null}
          onCerrar={() => setProgramando(false)}
        />
      )}

      <Cotizador
        abierto={cotizando}
        ordenServicioId={orden.id}
        ordenNumero={orden.numero}
        clienteNombre={orden.cliente?.nombre ?? "Sin cliente"}
        descripcionTrabajo={orden.descripcion}
        onCerrar={() => setCotizando(false)}
      />

      {/* Fuera del bloque de la cotización a propósito: el botón de cambiar
          cliente sale en cualquier orden, tenga cotización o no. */}
      <CambiarClienteDeLaOrden
        ordenServicioId={orden.id}
        clienteActual={orden.cliente?.nombre ?? "Sin cliente"}
        abierto={cambiandoCliente}
        onCerrar={() => setCambiandoCliente(false)}
      />

      {orden.cotizacion && (
        <>
          <Cotizador
            abierto={modificando}
            ordenServicioId={orden.id}
            ordenNumero={orden.numero}
            clienteNombre={orden.cliente?.nombre ?? "Sin cliente"}
            descripcionTrabajo={orden.descripcion}
            onCerrar={() => setModificando(false)}
            modificar={{
              cotizacionId: orden.cotizacion.id,
              numero: orden.cotizacion.numero_cotizacion,
            }}
          />

          {/* La hoja va fuera de la tarjeta: se dibuja en un portal y tapa la
              pantalla entera, como en Cotizaciones. */}
          {viendoObra && (
            <HojaDeLaObra orden={orden} onCerrar={() => setViendoObra(false)} />
          )}

          {viendoHoja && (
            <HojaDeLaOrden
              orden={orden}
              onCerrar={() => setViendoHoja(false)}
            />
          )}

          <AnularCotizacion
            abierto={anulando}
            cotizacionId={orden.cotizacion.id}
            numero={orden.cotizacion.numero_cotizacion}
            onCerrar={() => setAnulando(false)}
          />
        </>
      )}
    </>
  );
}
